import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom"; // Import useNavigate
import {
  Row,
  Col,
  Card,
  Form,
  FormGroup,
  Label,
  Input,
  CardBody,
  Container,
  Button,
  UncontrolledAlert,
  Spinner,
} from "reactstrap";
import Dropzone from "react-dropzone";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const ComplianceAssessment = () => {
  document.title = "Compliance Assessment";
  const navigate = useNavigate(); // Initialize navigate function


  const [selectedFiles, setSelectedFiles] = useState([]);
  const [assessmentType, setAssessmentType] = useState(""); // vendor or software
  const [entityName, setEntityName] = useState("");
  const [assessmentNotes, setAssessmentNotes] = useState("");
  const [complianceStandard, setComplianceStandard] = useState("");
  const [complianceVersion, setComplianceVersion] = useState("");
  const [assessmentDate, setAssessmentDate] = useState("");
  const [nextAssessmentDate, setNextAssessmentDate] = useState("");
  const [assessmentScope, setAssessmentScope] = useState("");
  const [riskLevel, setRiskLevel] = useState("low");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [userId] = useState(JSON.parse(localStorage.getItem("authUser"))?.username);

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState("success");
  const [loading, setLoading] = useState(false);

  const complianceStandards = {
    vendor: ["ISO 27001", "SOC 2", "GDPR", "HIPAA", "PCI DSS"],
    software: ["OWASP", "CIS Benchmarks", "NIST", "ISO 25010", "SOC 2"]
  };

  const handleAcceptedFiles = (files) => {
    files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("entityName", entityName);
    formData.append("complianceStandard", complianceStandard);
    formData.append("assessmentScope", assessmentScope);
    formData.append("riskLevel", riskLevel);
 

    selectedFiles.forEach((file) => {
      formData.append("documents", file);
    });

    try {
      const response = await fetch(
        `https://api.compliance.cx/tprm-assessment/${userId}/${assessmentType}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        setAlertMessage("Compliance assessment submitted successfully!");
        setAlertColor("success");
        setAlertVisible(true);

        // Reset form
        setEntityName("");
        setAssessmentNotes("");
        setComplianceStandard("");
        setComplianceVersion("");
        setAssessmentDate("");
        setNextAssessmentDate("");
        setAssessmentScope("");
        setRiskLevel("low");
        setContactName("");
        setContactEmail("");
        setSelectedFiles([]);

        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error("Failed to submit assessment.");
      }
    } catch (error) {
      setAlertMessage("Error 121. Please Try Later");
      setAlertColor("danger");
      setAlertVisible(true);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setAlertVisible(false);
      }, 10000);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Compliance" breadcrumbItem="Compliance Assessment" />
          <div className="mb-2"><Link to={-1} >
  ← Back
</Link></div>
          {alertVisible && (
            <UncontrolledAlert color={alertColor} className="alert-dismissible fade show">
              {alertMessage}
            </UncontrolledAlert>
          )}

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    {/* Assessment Type Selection */}
                    <FormGroup>
                      <Label for="assessmentType">Assessment Type</Label>
                      <Input
                        type="select"
                        id="assessmentType"
                        value={assessmentType}
                        onChange={(e) => setAssessmentType(e.target.value)}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="vendor">Vendor Assessment</option>
                        <option value="software">Software Assessment</option>
                      </Input>
                    </FormGroup>

                    {/* Entity Name */}
                    <FormGroup>
                      <Label for="entityName">
                        {assessmentType === "vendor" ? "Vendor Name" : "Software Name"}
                      </Label>
                      <Input
                        type="text"
                        id="entityName"
                        value={entityName}
                        onChange={(e) => setEntityName(e.target.value)}
                        placeholder={`Enter ${
                          assessmentType === "vendor" ? "vendor" : "software"
                        } name`}
                        required
                      />
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        {/* Compliance Standard */}
                        <FormGroup>
                          <Label for="complianceStandard">Compliance Standard</Label>
                          <Input
                            type="select"
                            id="complianceStandard"
                            value={complianceStandard}
                            onChange={(e) => setComplianceStandard(e.target.value)}
                            required
                          >
                            <option value="">Select Standard</option>
                            {assessmentType && 
                              complianceStandards[assessmentType].map((standard) => (
                                <option key={standard} value={standard}>
                                  {standard}
                                </option>
                              ))
                            }
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        {/* Risk Level */}
                        <FormGroup>
                          <Label for="riskLevel">Risk Level</Label>
                          <Input
                            type="select"
                            id="riskLevel"
                            value={riskLevel}
                            onChange={(e) => setRiskLevel(e.target.value)}
                            required
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      
                    </Row>

                  

                    {/* Assessment Scope */}
                    <FormGroup>
                      <Label for="assessmentScope">Assessment Scope</Label>
                      <Input
                        type="textarea"
                        id="assessmentScope"
                        rows="3"
                        value={assessmentScope}
                        onChange={(e) => setAssessmentScope(e.target.value)}
                        placeholder="Define the scope of this compliance assessment..."
                        required
                      />
                    </FormGroup>

                    

                    

              

                    {/* File Upload */}
                    <FormGroup>
                      <Label>Supporting Documents</Label>
                      <Dropzone onDrop={handleAcceptedFiles}>
                        {({ getRootProps, getInputProps }) => (
                          <div className="dropzone">
                            <div className="dz-message needsclick mt-2" {...getRootProps()}>
                              <input {...getInputProps()} />
                              <div className="mb-3">
                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                              </div>
                              <h4>Drop files here or click to upload evidence documents.</h4>
                            </div>
                          </div>
                        )}
                      </Dropzone>

                      <div className="dropzone-previews mt-3">
                        {selectedFiles.map((file, i) => (
                          <Card
                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                            key={`${file.name}-${i}`}
                          >
                            <div className="p-2">
                              <Row className="align-items-center">
                                <Col className="col-auto">
                                  <img
                                    height="80"
                                    className="avatar-sm rounded bg-light"
                                    alt={file.name}
                                    src={file.preview}
                                  />
                                </Col>
                                <Col>
                                  <p className="text-muted font-weight-bold mb-1">
                                    {file.name}
                                  </p>
                                  <p className="mb-0">
                                    <strong>{file.formattedSize}</strong>
                                  </p>
                                </Col>
                                <Col className="col-auto">
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => removeFile(i)}
                                  >
                                    ✕
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </FormGroup>

                    {/* Submit Button */}
                    <div className="text-center mt-4">
                      <Button type="submit" color="primary" disabled={loading || !assessmentType}>
                        {loading ? <Spinner size="sm" /> : "Submit Assessment"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ComplianceAssessment;